import type { ListedGathering } from "@/lib/clubs/gatherings";
import styles from "./GatheringsTimeline.module.scss";

export type StatusFilter = "all" | "upcoming" | "past" | "cancelled";

interface Props {
  gatherings: ListedGathering[];
  filter: StatusFilter;
  onFilterChange: (next: StatusFilter) => void;
  onSchedule: () => void;
  onSelect: (gatheringId: string) => void;
  totalCount: number;
}

interface DerivedGathering extends ListedGathering {
  status: "upcoming" | "past" | "cancelled";
}

function deriveStatus(g: ListedGathering, now: Date): DerivedGathering {
  if (g.cancelledAt) return { ...g, status: "cancelled" };
  if (new Date(g.date) < now) return { ...g, status: "past" };
  return { ...g, status: "upcoming" };
}

function relativeDay(iso: string, now: Date): string {
  const target = new Date(iso);
  const dayMs = 1000 * 60 * 60 * 24;
  const diff = Math.round(
    (target.getTime() - new Date(now.toDateString()).getTime()) / dayMs,
  );
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  if (diff > 1 && diff <= 7) return `In ${diff} days`;
  if (diff < -1 && diff >= -7) return `${-diff} days ago`;
  return target.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function longDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function shortTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function groupByMonth(gatherings: DerivedGathering[]) {
  const groups = new Map<string, DerivedGathering[]>();
  for (const g of gatherings) {
    const key = new Date(g.date).toLocaleString("en-US", {
      month: "long",
      year: "numeric",
    });
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(g);
  }
  return [...groups.entries()];
}

/**
 * Pure presentational timeline of a club's gatherings, grouped by month.
 * Graduated from the PR 1 v2 prototype. Caller owns data fetching, filtering
 * state, and click navigation.
 */
export function GatheringsTimeline({
  gatherings,
  filter,
  onFilterChange,
  onSchedule,
  onSelect,
  totalCount,
}: Props) {
  const now = new Date();
  const derived = gatherings.map((g) => deriveStatus(g, now));

  const filtered = derived.filter((g) => {
    if (filter === "all") return true;
    return g.status === filter;
  });

  const sorted = [...filtered].sort((a, b) => a.date.localeCompare(b.date));
  const groups = groupByMonth(sorted);

  const counts = {
    all: derived.length,
    upcoming: derived.filter((g) => g.status === "upcoming").length,
    past: derived.filter((g) => g.status === "past").length,
    cancelled: derived.filter((g) => g.status === "cancelled").length,
  };

  return (
    <div className={styles.shell}>
      <header className={styles.head}>
        <div>
          <p className={styles.eyebrow}>Gatherings</p>
          <h1 className={styles.title}>In-person meetups</h1>
          <p className={styles.sub}>
            {totalCount} {totalCount === 1 ? "gathering" : "gatherings"} ·
            timeline view
          </p>
        </div>
        <div className={styles.actions}>
          <div
            className={styles.filterRow}
            role="tablist"
            aria-label="Status filter"
          >
            <FilterChip
              label={`All (${counts.all})`}
              active={filter === "all"}
              onClick={() => onFilterChange("all")}
            />
            <FilterChip
              label={`Upcoming (${counts.upcoming})`}
              active={filter === "upcoming"}
              onClick={() => onFilterChange("upcoming")}
            />
            <FilterChip
              label={`Past (${counts.past})`}
              active={filter === "past"}
              onClick={() => onFilterChange("past")}
            />
            <FilterChip
              label={`Cancelled (${counts.cancelled})`}
              active={filter === "cancelled"}
              onClick={() => onFilterChange("cancelled")}
            />
          </div>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={onSchedule}
          >
            + Schedule gathering
          </button>
        </div>
      </header>

      {sorted.length === 0 && (
        <div className={styles.empty}>
          {filter === "all"
            ? "No gatherings scheduled yet. Create the first one."
            : `No ${filter} gatherings.`}
        </div>
      )}

      <div className={styles.timeline}>
        {groups.map(([month, items]) => (
          <section key={month} className={styles.monthBlock}>
            <header className={styles.monthHead}>
              <h2 className={styles.monthName}>{month}</h2>
              <span className={styles.monthCount}>{items.length}</span>
            </header>
            <ol className={styles.list}>
              {items.map((g) => {
                const startsAt = new Date(g.date);
                return (
                  <li
                    key={g.id}
                    className={[
                      styles.entry,
                      g.status === "cancelled" ? styles.entryCancelled : "",
                      g.status === "past" ? styles.entryPast : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <span className={styles.dateBlock}>
                      <span className={styles.dateDay}>
                        {startsAt.getDate()}
                      </span>
                      <span className={styles.dateWeek}>
                        {startsAt.toLocaleString("en-US", {
                          weekday: "short",
                        })}
                      </span>
                      <span className={styles.dateRel}>
                        {relativeDay(g.date, now)}
                      </span>
                    </span>
                    <span className={styles.dot} aria-hidden />
                    <article className={styles.card}>
                      <header className={styles.cardHead}>
                        <button
                          type="button"
                          className={styles.cardTitle}
                          onClick={() => onSelect(g.id)}
                        >
                          {g.title}
                        </button>
                        {g.status === "cancelled" && (
                          <span className={styles.statusPill}>Cancelled</span>
                        )}
                        {g.status === "past" && (
                          <span
                            className={`${styles.statusPill} ${styles.statusPast}`}
                          >
                            Past
                          </span>
                        )}
                      </header>
                      <p className={styles.meta}>
                        {longDate(g.date)} · {shortTime(g.date)}
                        {g.venueName && (
                          <>
                            <br />
                            {g.venueName}
                            {g.address && <> · {g.address}</>}
                            {!g.address && g.neighborhood && (
                              <> · {g.neighborhood}</>
                            )}
                          </>
                        )}
                      </p>
                      {g.description && (
                        <p className={styles.desc}>{g.description}</p>
                      )}
                      <footer className={styles.cardFoot}>
                        <span className={styles.rsvpRow}>
                          <span className={styles.rsvpItem}>
                            <strong>{g.rsvpCounts.GOING ?? 0}</strong> yes
                          </span>
                          <span className={styles.rsvpItem}>
                            <strong>{g.rsvpCounts.MAYBE ?? 0}</strong> maybe
                          </span>
                          <span className={styles.rsvpItem}>
                            <strong>{g.rsvpCounts.CANT ?? 0}</strong> no
                          </span>
                        </span>
                      </footer>
                    </article>
                  </li>
                );
              })}
            </ol>
          </section>
        ))}
      </div>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`${styles.chip} ${active ? styles.chipActive : ""}`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}
